import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import {
  getOfflineMap,
  saveZone,
  updateZone,
  deleteZone,
} from '../../utils/offlineDb';

function normalizeZoneKeys(zone) {
  let out = { ...zone };
  if (typeof out.strokeOpacity !== "undefined") {
    out.stroke_opacity = out.strokeOpacity;
    delete out.strokeOpacity;
  }
  if (typeof out.fillOpacity !== "undefined") {
    out.fill_opacity = out.fillOpacity;
    delete out.fillOpacity;
  }
  delete out.strokeOpacity;
  delete out.fillOpacity;
  return out;
}

export default function OfflineGeomanShapesSync({
  mapId,
  shapeLayerMap,
  allLayersById,
  onZonesChanged,
  onShapeClick,
  setIsGeomanActive,
  visibleZoneIds
}) {
  const map = useMap();
  const shapesLayer = useRef(null);

  useEffect(() => {
    if (!map) return;
    if (!shapesLayer.current) {
      shapesLayer.current = L.featureGroup().addTo(map);
    }
    return () => {
      if (shapesLayer.current) {
        map.removeLayer(shapesLayer.current);
        shapesLayer.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!mapId || !map || !shapesLayer.current) return;
    (async () => {
      try {
        const offlineMap = await getOfflineMap(mapId);
        const zones = Array.isArray(offlineMap?.zones) ? offlineMap.zones : [];
        shapesLayer.current.clearLayers();
        if (shapeLayerMap && shapeLayerMap.current) shapeLayerMap.current = {};
        if (allLayersById && allLayersById.current) allLayersById.current = {};
        zones.forEach(zone => {
          let geometry;
          try {
            geometry = typeof zone.geometry === 'string' ? JSON.parse(zone.geometry) : zone.geometry;
          } catch {
            return;
          }
          let layer;
          const stroke_opacity = typeof zone.stroke_opacity !== 'undefined'
            ? zone.stroke_opacity
            : (typeof zone.strokeOpacity !== 'undefined' ? zone.strokeOpacity : 1.0);
          const fill_opacity = typeof zone.fill_opacity !== 'undefined'
            ? zone.fill_opacity
            : (typeof zone.fillOpacity !== 'undefined' ? zone.fillOpacity : 0.5);
          if (geometry.type === "Point" && typeof zone.radius === "number") {
            const coords = geometry.coordinates;
            layer = L.circle([coords[1], coords[0]], {
              radius: zone.radius,
              color: zone.strokeColor || '#ff0000',
              fillColor: zone.fillColor || '#ff0000',
              opacity: stroke_opacity,
              fillOpacity: fill_opacity,
              weight: 3,
            });
          } else {
            const geojsonFeature = {
              type: 'Feature',
              geometry,
              properties: {},
            };
            const layers = L.geoJSON(geojsonFeature, {
              style: {
                color: zone.strokeColor || '#ff0000',
                fillColor: zone.fillColor || '#ff0000',
                opacity: stroke_opacity,
                fillOpacity: fill_opacity,
                weight: 3,
              },
            }).getLayers();
            layer = layers[0];
          }
          if (!layer) return;
          layer._dbId = zone.id;
          layer.options.label = zone.label || '';
          layer.options.color = zone.strokeColor || '#ff0000';
          layer.options.fillColor = zone.fillColor || '#ff0000';
          layer.options.opacity = stroke_opacity;
          layer.options.fillOpacity = fill_opacity;
          if (shapeLayerMap && shapeLayerMap.current) {
            shapeLayerMap.current[zone.id] = layer;
          }
          if (allLayersById && allLayersById.current) {
            allLayersById.current[zone.id] = layer;
          }
          layer.off('click');
          layer.on('click', (e) => {
            if (setIsGeomanActive) setIsGeomanActive(true);
            if (e.originalEvent) e.originalEvent._shapeClick = true;
            if (onShapeClick) onShapeClick(zone, layer);
          });
          const shouldBeVisible = !visibleZoneIds || visibleZoneIds.has(Number(zone.id));
          if (shouldBeVisible) {
            shapesLayer.current.addLayer(layer);
          }
        });
        if (!map.hasLayer(shapesLayer.current)) {
          shapesLayer.current.addTo(map);
        }
      } catch (e) {
      }
    })();
  }, [mapId, map, onShapeClick, shapeLayerMap, allLayersById, setIsGeomanActive, visibleZoneIds]);

  useEffect(() => {
    if (!map || !shapesLayer.current) return;
    const onCreate = async (e) => {
      const layer = e.layer;
      let geometry = layer.toGeoJSON().geometry;
      let radius = null;
      let shapeType = geometry.type || '';
      if (typeof layer.getRadius === 'function') {
        const center = layer.getLatLng();
        geometry = {
          type: 'Point',
          coordinates: [center.lng, center.lat],
        };
        radius = layer.getRadius();
        shapeType = 'Circle';
      }
      const zone = {
        id: Date.now(),
        mapId,
        label: '',
        geometry: JSON.stringify(geometry),
        fillColor: layer.options.fillColor || '#ff0000',
        strokeColor: layer.options.color || '#ff0000',
        stroke_opacity: typeof layer.options.opacity !== 'undefined' ? layer.options.opacity : 1.0,
        fill_opacity: typeof layer.options.fillOpacity !== 'undefined' ? layer.options.fillOpacity : 0.5,
        pattern: '',
        radius,
        shapeType,
      };
      const cleanZone = normalizeZoneKeys(zone);
      try {
        await saveZone(mapId, cleanZone);
        layer._dbId = cleanZone.id;
        if (shapeLayerMap && shapeLayerMap.current) {
          shapeLayerMap.current[cleanZone.id] = layer;
        }
        if (allLayersById && allLayersById.current) {
          allLayersById.current[cleanZone.id] = layer;
        }
        shapesLayer.current.addLayer(layer);
        // WAIT for saveZone before calling onZonesChanged
        if (onZonesChanged) setTimeout(onZonesChanged, 10);
      } catch (err) {
      }
    };

    const onEdit = async (e) => {
      const promises = [];
      e.layers.eachLayer((layer) => {
        if (!layer._dbId) return;
        let geometry = layer.toGeoJSON().geometry;
        let radius = null;
        let shapeType = geometry.type || '';
        if (typeof layer.getRadius === 'function') {
          const center = layer.getLatLng();
          geometry = {
            type: 'Point',
            coordinates: [center.lng, center.lat],
          };
          radius = layer.getRadius();
          shapeType = 'Circle';
        }
        const zone = {
          id: layer._dbId,
          mapId,
          label: layer.options.label || '',
          geometry: JSON.stringify(geometry),
          fillColor: layer.options.fillColor || '#ff0000',
          strokeColor: layer.options.color || '#ff0000',
          stroke_opacity: typeof layer.options.opacity !== 'undefined' ? layer.options.opacity : 1.0,
          fill_opacity: typeof layer.options.fillOpacity !== 'undefined' ? layer.options.fillOpacity : 0.5,
          pattern: '',
          radius,
          shapeType,
        };
        const cleanZone = normalizeZoneKeys(zone);
        promises.push(updateZone(mapId, cleanZone));
      });
      try {
        await Promise.all(promises);
        if (onZonesChanged) setTimeout(onZonesChanged, 10);
      } catch {}
    };

    const onRemove = async (e) => {
      if (!e.layer._dbId) return;
      try {
        await deleteZone(mapId, e.layer._dbId);
        if (shapeLayerMap && shapeLayerMap.current) {
          delete shapeLayerMap.current[e.layer._dbId];
        }
        if (allLayersById && allLayersById.current) {
          delete allLayersById.current[e.layer._dbId];
        }
        shapesLayer.current.removeLayer(e.layer);
        // WAIT for deleteZone before calling onZonesChanged
        if (onZonesChanged) setTimeout(onZonesChanged, 10);
      } catch {}
    };

    map.on('pm:create', onCreate);
    map.on('pm:edit', onEdit);
    map.on('pm:remove', onRemove);

    return () => {
      map.off('pm:create', onCreate);
      map.off('pm:edit', onEdit);
      map.off('pm:remove', onRemove);
    };
  }, [map, mapId, onZonesChanged, shapeLayerMap, allLayersById]);

  return null;
}
